import { useEffect, useState, useMemo } from "react";
import API from "../services/api";

export default function Dashboard() {
  const [claims, setClaims] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [managers, setManagers] = useState([]);

  const [selectedDealer, setSelectedDealer] = useState("");
  const [selectedManager, setSelectedManager] = useState("");

  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState("");

  const [search, setSearch] = useState("");
  const [date, setDate] = useState("");

  const [stats, setStats] = useState({});

  // ================= INIT =================
  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
        window.location = "/login";
        return;
      }

      const payload = JSON.parse(atob(token.split(".")[1]));
      const userRole = String(payload.role || "").toLowerCase().trim();

      setRole(userRole);
      await loadAll(userRole);
    };

    init();
  }, []);

  // ================= LOAD =================
  const loadAll = async (userRole) => {
    try {
      setLoading(true);

      await Promise.all([
        userRole !== "finance" ? loadClaims(userRole) : Promise.resolve(),
        loadStats(userRole),
        userRole === "manager" ? loadDealers() : Promise.resolve(),
        userRole === "ceo" ? loadManagers() : Promise.resolve()
      ]);
    } finally {
      setLoading(false);
    }
  };

  // ================= CLAIMS =================
  const loadClaims = async (userRole, managerId = "") => {
    let url = "";

    if (userRole === "manager") url = "/manager/claims";
    else if (userRole === "ceo")
      url = `/ceo/claims${managerId ? `?manager_id=${managerId}` : ""}`;
    else if (userRole === "admin") url = "/admin/claims";

    if (!url) return;

    const res = await API.get(url);
    setClaims(res.data || []);
  };

  // ================= STATS =================
  const loadStats = async (userRole, managerId = "") => {
    let url = "";

    if (userRole === "manager") url = "/claims/dashboard-stats";
    else if (userRole === "ceo")
      url = `/ceo/dashboard${managerId ? `?manager_id=${managerId}` : ""}`;
    else if (userRole === "admin") url = "/dashboard";

    // ✅ FIXED FINANCE
    else if (userRole === "finance") url = "/finance/dashboard";

    if (!url) return;

    const res = await API.get(url);

    console.log("DASHBOARD STATS:", res.data);

    setStats(res.data || {});
  };

  const loadDealers = async () => {
    const res = await API.get("/manager/dealers");
    setDealers(res.data || []);
  };

  const loadManagers = async () => {
    const res = await API.get("/ceo/managers");
    setManagers(res.data || []);
  };

  const handleManagerChange = async (value) => {
    setSelectedManager(value);

    await Promise.all([
      loadStats("ceo", value),
      loadClaims("ceo", value)
    ]);
  };

  // ================= FILTER =================
  const filteredClaims = useMemo(() => {
    return claims.filter((c) => {
      return (
        (!selectedDealer || c.dealer_id == selectedDealer) &&
        (!search ||
          c.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
          c.customer_mobile?.includes(search)) &&
        (!date ||
          new Date(c.created_at).toISOString().slice(0, 10) === date)
      );
    });
  }, [claims, selectedDealer, search, date]);

  const format = (num) => Number(num || 0).toLocaleString();

  // ✅ FINANCE VALUES
  const total = stats.total_claim_amount || 0;
  const paid = stats.total_paid || 0;
  const pending = stats.total_pending || 0;

  // ================= UI =================
  return (
    <div style={{ padding: 20 }}>
      <h2>📊 {role.toUpperCase()} Dashboard</h2>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>


        
          {/* ================= FINANCE DASHBOARD ================= */}
          {role === "finance" ? (
            <div style={styles.cards}>
              <Card title="Total Claims Amount" value={`Rs ${format(total)}`} />
              <Card title="Paid" value={`Rs ${format(paid)}`} green />
              <Card title="Pending" value={`Rs ${format(pending)}`} orange />
            </div>
          ) : (
            <>
              {/* CEO FILTER */}
              {role === "ceo" && (
                <div style={styles.filters}>
                  <select onChange={(e) => handleManagerChange(e.target.value)}>
                    <option value="">All Managers</option>
                    {managers.map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* CARDS */}
              <div style={styles.cards}>
                <Card title="Total Claims" value={stats.totalClaims} />
                <Card title="Approved" value={stats.approvedCount} green />
                <Card title="Pending" value={stats.pendingCount} orange />
                <Card title="Rejected" value={stats.rejectedCount} red />
              </div>

              {/* TABLE */}
              <div style={{ overflowX: "auto", marginTop: 20 }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th>Salesman</th>
                      <th>Dealer</th>
                      <th>Customer</th>
                      <th>Mobile</th>
                      <th>Product</th>
                      <th>Date</th>
                      <th>Amount</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredClaims.map((c) => (
                      <tr key={c.id}>
                        <td>{c.salesman_name}</td>
                        <td>{c.dealer_name}</td>
                        <td>{c.customer_name}</td>
                        <td>{c.customer_mobile}</td>
                        <td>{c.qr_value}</td>
                        <td>{new Date(c.created_at).toLocaleDateString()}</td>
                        <td>Rs {format(c.claim_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

// ================= CARD =================
const Card = ({ title, value, green, orange, red }) => (
  <div style={{
    ...styles.card,
    background: green ? "#27ae60" :
               orange ? "#f39c12" :
               red ? "#e74c3c" :
               "#2c5178"
  }}>
    <p>{title}</p>
    <h2>{value}</h2>
  </div>
);

// ================= STYLES =================
const styles = {
  cards: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    marginBottom: 20
  },
  card: {
    flex: 1,
    minWidth: 150,
    padding: 15,
    color: "#fff",
    borderRadius: 10,
    textAlign: "center"
  },
  filters: {
    display: "flex",
    gap: 10,
    marginBottom: 15
  },
  table: {
    width: "100%",
    borderCollapse: "collapse"
  }
};